import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch, type DenialDetail } from "@/api/client";
import {
  docgenResponseSchema,
  type DocgenResponse,
  type DocumentGenerationRequest,
  type DocumentSection,
} from "@/api/schemas";

/**
 * Sprint B — W2.
 *
 * Drives the ``POST /api/generate-documents/stream`` SSE endpoint.
 *
 * EventSource only supports GET, so we use ``fetch`` with a streaming
 * ``ReadableStream`` body and parse SSE frames by hand. The frame
 * grammar (one ``event:`` line + one ``data:`` line + blank-line
 * terminator) is small enough to fit in this file without pulling a
 * dependency.
 *
 * The hook surfaces the per-section lifecycle so the UI can render a
 * row-by-row progress list (see :func:`GeneratingProgress`).
 *
 * Set ``VITE_USE_STREAMING=false`` to fall back to the non-streaming
 * endpoint (used by the fixture-only kitchen-sink page and as an
 * escape hatch for corporate proxies that buffer SSE).
 */

export type SectionStatus = "queued" | "running" | "done" | "error";

export interface SectionProgress {
  section_id: string;
  title: string;
  status: SectionStatus;
  /** Populated on ``section_completed``. */
  section?: DocumentSection;
  /** Per-section validation surfaced once on the ``done`` event. */
  validation_errors?: Array<{ message: string; error_type?: string }>;
  validation_warnings?: Array<{ message: string }>;
}

export interface StreamingState {
  status: "idle" | "streaming" | "done" | "error";
  /** ``section_id -> progress`` so render order is decoupled from arrival order. */
  sections: Record<string, SectionProgress>;
  /** Insertion order — used by the GeneratingProgress component to keep a stable layout. */
  sectionOrder: string[];
  /** Final ``done`` payload — ``null`` until the stream resolves. */
  result: DocgenResponse | null;
  error: string | null;
  /** L8 denial-mode payload set when the backend returns HTTP 422 with
   *  ``error_code = "missing_mandatory_law_text"`` before the SSE
   *  generator opens. ``null`` for any other failure path. */
  denial: DenialDetail | null;
  /** Wall-clock seconds since the stream started; ticks every 250 ms while streaming. */
  elapsedSeconds: number;
}

interface SectionStartedEvent {
  section_id: string;
  title: string;
}

interface SectionCompletedEvent {
  section_id: string;
  section: DocumentSection;
  validation_errors?: SectionProgress["validation_errors"];
  validation_warnings?: SectionProgress["validation_warnings"];
}

interface DoneEvent extends DocgenResponse {
  section_validation_index?: Record<
    string,
    {
      errors: SectionProgress["validation_errors"];
      warnings: SectionProgress["validation_warnings"];
    }
  >;
}

const INITIAL_STATE: StreamingState = {
  status: "idle",
  sections: {},
  sectionOrder: [],
  result: null,
  error: null,
  denial: null,
  elapsedSeconds: 0,
};

export function useStreamingDocgen() {
  const [state, setState] = useState<StreamingState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<number | null>(null);

  // Tick the clock so the UI shows live elapsed time without re-rendering
  // on every SSE frame. 250 ms is fine-grained enough for "feels live"
  // without being expensive.
  useEffect(() => {
    if (state.status !== "streaming") return undefined;
    const id = window.setInterval(() => {
      const startedAt = startedAtRef.current;
      if (startedAt == null) return;
      setState((s) =>
        s.status === "streaming"
          ? { ...s, elapsedSeconds: (Date.now() - startedAt) / 1000 }
          : s,
      );
    }, 250);
    return () => window.clearInterval(id);
  }, [state.status]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    startedAtRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const start = useCallback(async (payload: DocumentGenerationRequest) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    startedAtRef.current = Date.now();
    setState({ ...INITIAL_STATE, status: "streaming" });

    try {
      const res = await apiFetch("/api/generate-documents/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        // L8 denial mode: the backend returns plain JSON 422 *before*
        // opening the SSE channel when an export-blocking law is
        // missing text. Surface it as a structured ``denial`` state so
        // the UI can render the hard-error banner with the missing
        // instruments listed.
        if (res.status === 422) {
          let parsed: unknown = null;
          try {
            parsed = await res.json();
          } catch {
            // not JSON — fall through to the generic error path below
          }
          const detail =
            parsed && typeof parsed === "object" && parsed !== null
              ? (parsed as { detail?: unknown }).detail
              : null;
          if (
            detail &&
            typeof detail === "object" &&
            (detail as { error_code?: string }).error_code ===
              "missing_mandatory_law_text"
          ) {
            const denial = detail as DenialDetail;
            setState((s) => ({
              ...s,
              status: "error",
              error: denial.message,
              denial,
            }));
            return;
          }
        }
        throw new Error(
          `Streaming endpoint returned ${res.status} ${res.statusText}`,
        );
      }
      await consumeSseStream(res.body, (event, data) => {
        applyEvent(setState, event, data);
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      setState((s) => ({ ...s, status: "error", error: message }));
    }
  }, []);

  // Cancel any in-flight stream when the consumer unmounts so we don't
  // leak the connection.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { ...state, start, reset } as const;
}

// ---------------------------------------------------------------------------
// SSE byte-stream parser
// ---------------------------------------------------------------------------

/**
 * Read a ``ReadableStream`` of SSE bytes and invoke ``onEvent`` for each
 * fully-formed frame. Buffers across chunk boundaries so a frame split
 * across two TCP packets isn't dropped.
 *
 * Comment frames (lines starting with ``:``) are skipped — we use them
 * server-side as a heartbeat to keep nginx-style proxies from killing
 * the connection.
 */
async function consumeSseStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: unknown) => void,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separator = buffer.indexOf("\n\n");
    while (separator !== -1) {
      const block = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const parsed = parseSseBlock(block);
      if (parsed) onEvent(parsed.event, parsed.data);
      separator = buffer.indexOf("\n\n");
    }
  }
}

function parseSseBlock(
  block: string,
): { event: string; data: unknown } | null {
  let event = "";
  let dataLine = "";
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice("event:".length).trim();
    else if (line.startsWith("data:")) dataLine = line.slice("data:".length).trim();
  }
  if (!event || !dataLine) return null;
  try {
    return { event, data: JSON.parse(dataLine) };
  } catch {
    return { event, data: dataLine };
  }
}

// ---------------------------------------------------------------------------
// Event reducer
// ---------------------------------------------------------------------------

function applyEvent(
  setState: React.Dispatch<React.SetStateAction<StreamingState>>,
  event: string,
  data: unknown,
): void {
  switch (event) {
    case "section_started":
      setState((s) => sectionStarted(s, data as SectionStartedEvent));
      return;
    case "section_completed":
      setState((s) => sectionCompleted(s, data as SectionCompletedEvent));
      return;
    case "done":
      setState((s) => streamDone(s, data as DoneEvent));
      return;
    case "error":
      setState((s) => ({
        ...s,
        status: "error",
        error: (data as { message?: string })?.message ?? "Greška na serveru",
      }));
      return;
    default:
      // Forward-compat: ignore unknown events so the backend can add new
      // ones without breaking older clients.
      return;
  }
}

function sectionStarted(
  state: StreamingState,
  payload: SectionStartedEvent,
): StreamingState {
  if (state.sections[payload.section_id]) return state;
  return {
    ...state,
    sectionOrder: [...state.sectionOrder, payload.section_id],
    sections: {
      ...state.sections,
      [payload.section_id]: {
        section_id: payload.section_id,
        title: payload.title,
        status: "running",
      },
    },
  };
}

function sectionCompleted(
  state: StreamingState,
  payload: SectionCompletedEvent,
): StreamingState {
  const existing = state.sections[payload.section_id] ?? {
    section_id: payload.section_id,
    title: payload.section?.title ?? payload.section_id,
    status: "running" as const,
  };
  return {
    ...state,
    sectionOrder: state.sectionOrder.includes(payload.section_id)
      ? state.sectionOrder
      : [...state.sectionOrder, payload.section_id],
    sections: {
      ...state.sections,
      [payload.section_id]: {
        ...existing,
        status: "done",
        section: payload.section,
        validation_errors: payload.validation_errors,
        validation_warnings: payload.validation_warnings,
      },
    },
  };
}

function streamDone(state: StreamingState, payload: DoneEvent): StreamingState {
  // The ``done`` payload IS the full DocgenResponse. Strip the streaming-
  // only ``section_validation_index`` before validating against the
  // shared response schema; merge the per-section errors/warnings back
  // into the per-row progress so the UI doesn't have to re-walk the
  // global validation arrays.
  const { section_validation_index: index, ...rest } = payload;
  const parsed = docgenResponseSchema.safeParse(rest);
  if (!parsed.success) {
    return {
      ...state,
      status: "error",
      error: `Stream završen, ali odgovor nije validan: ${parsed.error.message}`,
    };
  }

  const merged: Record<string, SectionProgress> = { ...state.sections };
  if (index) {
    for (const [sid, slice] of Object.entries(index)) {
      const existing = merged[sid];
      if (!existing) continue;
      merged[sid] = {
        ...existing,
        validation_errors: slice.errors ?? [],
        validation_warnings: slice.warnings ?? [],
      };
    }
  }

  const elapsed =
    typeof parsed.data.elapsed_seconds === "number"
      ? parsed.data.elapsed_seconds
      : state.elapsedSeconds;

  return {
    ...state,
    status: "done",
    result: parsed.data,
    sections: merged,
    elapsedSeconds: elapsed,
  };
}

// ---------------------------------------------------------------------------
// Feature flag
// ---------------------------------------------------------------------------

/** Read ``VITE_USE_STREAMING`` (default: enabled). */
export function streamingEnabled(): boolean {
  const raw = import.meta.env.VITE_USE_STREAMING;
  if (typeof raw === "string") return raw.toLowerCase() !== "false";
  return true;
}
